import { useMemo } from 'react'
import styles from './PasswordStrength.module.css'

interface PasswordStrengthProps {
  password: string
}

interface StrengthResult {
  score: number // 0-4
  label: string
  color: string
  checks: {
    length: boolean
    lowercase: boolean
    uppercase: boolean
    number: boolean
    special: boolean
  }
}

function calculateStrength(password: string): StrengthResult {
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }

  const passedChecks = Object.values(checks).filter(Boolean).length
  let score: number
  let label: string
  let color: string

  if (password.length === 0) {
    return { score: 0, label: '', color: 'transparent', checks }
  }

  if (passedChecks >= 5 && password.length >= 12) {
    score = 4
    label = 'Very Strong'
    color = '#4E9A6A'
  } else if (passedChecks >= 4) {
    score = 3
    label = 'Strong'
    color = '#84cc16'
  } else if (passedChecks >= 3) {
    score = 2
    label = 'Fair'
    color = '#C08A3C'
  } else if (passedChecks >= 2) {
    score = 1
    label = 'Weak'
    color = '#C8A45C'
  } else {
    score = 0
    label = 'Very Weak'
    color = '#C4554D'
  }

  return { score, label, color, checks }
}

export default function PasswordStrength({ password }: PasswordStrengthProps) {
  const strength = useMemo(() => calculateStrength(password), [password])

  if (!password) return null

  return (
    <div className={styles.container}>
      {/* Strength bar */}
      <div className={styles.barContainer}>
        <div className={styles.barTrack}>
          {[0, 1, 2, 3, 4].map((index) => (
            <div
              key={index}
              className={styles.barSegment}
              style={{
                backgroundColor: index <= strength.score ? strength.color : 'rgba(255, 255, 255, 0.1)',
              }}
            />
          ))}
        </div>
        <span className={styles.label} style={{ color: strength.color }}>
          {strength.label}
        </span>
      </div>

      {/* Requirements checklist */}
      <div className={styles.requirements}>
        <Requirement met={strength.checks.length} text="At least 8 characters" />
        <Requirement met={strength.checks.lowercase} text="Lowercase letter" />
        <Requirement met={strength.checks.uppercase} text="Uppercase letter" />
        <Requirement met={strength.checks.number} text="Number" />
        <Requirement met={strength.checks.special} text="Special character" />
      </div>
    </div>
  )
}

function Requirement({ met, text }: { met: boolean; text: string }) {
  return (
    <div className={styles.requirement}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke={met ? '#4E9A6A' : '#7A8189'}
        strokeWidth="2"
      >
        {met ? (
          <polyline points="20 6 9 17 4 12" />
        ) : (
          <circle cx="12" cy="12" r="10" />
        )}
      </svg>
      <span style={{ color: met ? '#8B9299' : '#7A8189' }}>{text}</span>
    </div>
  )
}

// Export for use in form validation
export function isPasswordStrong(password: string): boolean {
  const strength = calculateStrength(password)
  return strength.score >= 3 // At least "Strong"
}
